import type { ThemeAwareColor } from '@shared/schemas/chart';

// Colors optimized for dark backgrounds (high contrast, bright colors)
export const DARK_BACKGROUND_COLORS = [
  '#60a5fa', // Bright blue
  '#34d399', // Bright emerald  
  '#fbbf24', // Bright amber
  '#f87171', // Bright red
  '#a78bfa', // Bright violet
  '#06b6d4', // Bright cyan
  '#84cc16', // Bright lime
  '#f472b6', // Bright pink
  '#fb923c', // Bright orange
  '#10b981', // Bright green
  '#8b5cf6', // Bright purple
  '#ef4444', // Bright red-500
];

// Colors optimized for light backgrounds (darker, more saturated)
export const LIGHT_BACKGROUND_COLORS = [
  '#1d4ed8', // Dark blue
  '#059669', // Dark emerald
  '#d97706', // Dark amber
  '#dc2626', // Dark red
  '#7c3aed', // Dark violet
  '#0891b2', // Dark cyan
  '#65a30d', // Dark lime
  '#db2777', // Dark pink
  '#ea580c', // Dark orange
  '#047857', // Dark green
  '#6d28d9', // Dark purple
  '#b91c1c', // Dark red-700
];

// Universal colors that work well on both light and dark backgrounds
export const UNIVERSAL_COLORS = [
  '#3b82f6', // Blue-500 (good contrast on both)
  '#10b981', // Emerald-500 
  '#f59e0b', // Amber-500
  '#ef4444', // Red-500
  '#8b5cf6', // Violet-500
  '#06b6d4', // Cyan-500
  '#84cc16', // Lime-500
  '#ec4899', // Pink-500
  '#f97316', // Orange-500
  '#22c55e', // Green-500
  '#a855f7', // Purple-500
  '#e11d48', // Rose-500
];

// Pre-defined theme-aware color combinations
export const THEME_AWARE_COLOR_PAIRS: ThemeAwareColor[] = [
  { light: '#1d4ed8', dark: '#60a5fa' }, // Blue pair
  { light: '#059669', dark: '#34d399' }, // Emerald pair
  { light: '#d97706', dark: '#fbbf24' }, // Amber pair
  { light: '#dc2626', dark: '#f87171' }, // Red pair
  { light: '#7c3aed', dark: '#a78bfa' }, // Violet pair
  { light: '#0891b2', dark: '#06b6d4' }, // Cyan pair
  { light: '#65a30d', dark: '#84cc16' }, // Lime pair
  { light: '#db2777', dark: '#f472b6' }, // Pink pair
  { light: '#ea580c', dark: '#fb923c' }, // Orange pair
  { light: '#047857', dark: '#10b981' }, // Green pair
  { light: '#6d28d9', dark: '#8b5cf6' }, // Purple pair
  { light: '#b91c1c', dark: '#ef4444' }, // Deep red pair
];

export interface ThemeAwareColorSection {
  id: 'dark' | 'light' | 'universal';
  title: string;
  description: string;
  colors: string[] | ThemeAwareColor[];
}

export const COLOR_SECTIONS: ThemeAwareColorSection[] = [
  {
    id: 'dark',
    title: 'Dark Backgrounds',
    description: 'Optimized for dark themes',
    colors: DARK_BACKGROUND_COLORS,
  },
  {
    id: 'light', 
    title: 'Light Backgrounds',
    description: 'Optimized for light themes',
    colors: LIGHT_BACKGROUND_COLORS,
  },
  {
    id: 'universal',
    title: 'Universal',
    description: 'Works on both themes',
    colors: THEME_AWARE_COLOR_PAIRS,
  },
];

// Helper function to get a color string for display purposes
export const getDisplayColor = (color: string | ThemeAwareColor, preferredTheme: 'light' | 'dark' = 'light'): string => {
  if (typeof color === 'string') {
    return color;
  }
  return color[preferredTheme];
};

// Helper function to create a theme-aware color from universal selection
export const createThemeAwareColor = (lightColor: string, darkColor: string): ThemeAwareColor => {
  return { light: lightColor, dark: darkColor };
};

// Function to get the appropriate color for current theme
export const getColorForCurrentTheme = (
  color: string | ThemeAwareColor, 
  isDarkMode: boolean
): string => {
  if (typeof color === 'string') {
    return color;
  }
  return isDarkMode ? color.dark : color.light;
}; 